import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LOGO_URL_TRANSPARENT } from "@/lib/config";
import { Menu, X, Phone, Mail } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/booking", label: "Book" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  React.useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 glass border-b border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3">
          <img src={LOGO_URL_TRANSPARENT} alt="Jfeva LLC" className="h-11 w-11 object-contain" />
          <div className="leading-tight">
            <div className="font-display text-lg text-[color:var(--brand-ink)]">JFeva Solutions, LLC</div>
            <div className="eyebrow text-[color:var(--brand-wood)]">Flooring &amp; Tile</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-[color:var(--brand-ink)]"
                    : "text-[color:var(--brand-woodDark)] hover:text-[color:var(--brand-ink)]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:8502520162"
            data-testid="header-phone"
            className="hidden xl:inline-flex items-center gap-2 text-sm text-[color:var(--brand-woodDark)] hover:text-[color:var(--brand-ink)]"
          >
            <Phone className="h-4 w-4" /> 850-252-0162
          </a>
          <Link
            to="/quote"
            data-testid="header-quote-cta"
            className="inline-flex items-center rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow]"
          >
            Request Quote
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2 text-[color:var(--brand-ink)]"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/5 bg-[color:var(--brand-cream)]">
          <nav className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mnav-${n.label.toLowerCase()}`}
                className="py-2 text-base text-[color:var(--brand-ink)]"
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              data-testid="mnav-quote-cta"
              className="mt-2 inline-flex justify-center items-center rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-5 py-3 text-sm font-medium"
            >
              Request Quote
            </Link>
            <a href="tel:8502520162" className="flex items-center gap-2 py-2 text-sm text-[color:var(--brand-woodDark)]">
              <Phone className="h-4 w-4" /> 850-252-0162
            </a>
            <a href="mailto:info@jfeva.com" className="flex items-center gap-2 py-2 text-sm text-[color:var(--brand-woodDark)]">
              <Mail className="h-4 w-4" /> info@jfeva.com
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
